// src/excel/builders/aha/buildWorkbook.js
// XLSX builder for the AHA service

export function buildAhaWorkbook(XLSX, params) {
  const {
    t,
    shortcode,
    stationName,
    serviceName,
    matrix = [],
    hours = [],
    meta = null
  } = params || {};

  const wb = XLSX.utils.book_new();
  const ws = {};
  const merges = [];

  const brandText = (t && t('listeners.brand_line_excel')) || 'Radiomonitor.Net';
  const serviceTitle = serviceName || (t && t('aha.title')) || 'Average daily listening profile by weekday';
  const stationTitle = stationName || shortcode || '';
  const endDate = meta?.period?.end_date || '';
  const months = meta?.period?.months;
  const timezone = meta?.tz || 'Europe/Kyiv';
  const totalColumns = Math.max(1, (Array.isArray(hours) && hours.length ? hours.length + 1 : 25));

  let currentRow = 0;

  // Brand row
  XLSX.utils.sheet_add_aoa(ws, [[brandText]], { origin: { r: currentRow, c: 0 } });
  merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: totalColumns - 1 } });
  const brandAddr = XLSX.utils.encode_cell({ r: currentRow, c: 0 });
  ws[brandAddr] = ws[brandAddr] || {};
  ws[brandAddr].s = {
    font: { bold: true, sz: 20, color: { rgb: '1F2A5A' } },
    fill: { fgColor: { rgb: 'EEF2FF' } },
    alignment: { horizontal: 'left', vertical: 'center' }
  };
  currentRow += 2;

  const infoRows = [
    [serviceTitle],
    ...(stationTitle ? [[stationTitle]] : []),
    ...(endDate ? [[`${t ? t('aha.end_date') : 'Period end date'}: ${endDate}`]] : []),
    ...(months ? [[`${t ? t('aha.depth') : 'Depth'}: ${months}`]] : []),
    [[`${t ? t('aha.timezone', 'Time zone') : 'Time zone'}: ${timezone}`]],
    [[(t && t('aha.method_legend')) || 'Method: average per minute points → average per hour → average of identical slots (weekday, hour).']],
    ['']
  ];

  XLSX.utils.sheet_add_aoa(ws, infoRows, { origin: { r: currentRow, c: 0 } });
  for (let i = 0; i < infoRows.length; i += 1) {
    merges.push({ s: { r: currentRow + i, c: 0 }, e: { r: currentRow + i, c: totalColumns - 1 } });
  }

  const encode = XLSX.utils.encode_cell;
  const serviceAddr = encode({ r: currentRow, c: 0 });
  if (ws[serviceAddr]) {
    ws[serviceAddr].s = {
      font: { bold: true, sz: 18, color: { rgb: '1F1F1F' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    };
  }
  if (stationTitle) {
    const stationAddr = encode({ r: currentRow + 1, c: 0 });
    if (ws[stationAddr]) {
      ws[stationAddr].s = {
        font: { bold: true, sz: 16, color: { rgb: '1F1F1F' } },
        alignment: { horizontal: 'left', vertical: 'center' }
      };
    }
  }

  currentRow += infoRows.length;

  const hourLabels = Array.isArray(hours) && hours.length
    ? hours.map((h) => String(h).padStart(2, '0'))
    : Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

  const weekdayOrder = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const weekdayLabels = weekdayOrder.map((key, idx) => {
    const label = t && t(`aha.weekdays.${key}`);
    if (label && label !== `aha.weekdays.${key}`) return label;
    const fallback = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][idx];
    return fallback;
  });

  const headerRow = ['Day/Hour', ...hourLabels];
  const dataRows = (matrix || []).map((row = [], idx) => [
    weekdayLabels[idx] || `D${idx + 1}`,
    ...hourLabels.map((_, hIdx) => {
      const val = row[hIdx];
      const num = Number(val);
      return Number.isFinite(num) ? num : Number(val || 0) || 0;
    })
  ]);

  const tableBlock = [headerRow, ...dataRows];
  XLSX.utils.sheet_add_aoa(ws, tableBlock, { origin: { r: currentRow, c: 0 } });

  const headerRowIndex = currentRow;
  const totalRowIndex = currentRow + tableBlock.length - 1;

  const headerStyle = {
    font: { bold: true, color: { rgb: '333333' } },
    fill: { fgColor: { rgb: 'F0F0F0' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: 'D0D0D0' } },
      bottom: { style: 'thin', color: { rgb: 'D0D0D0' } },
      left: { style: 'thin', color: { rgb: 'D0D0D0' } },
      right: { style: 'thin', color: { rgb: 'D0D0D0' } }
    }
  };

  const borderStyle = {
    border: {
      top: { style: 'thin', color: { rgb: 'E0E0E0' } },
      bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
      left: { style: 'thin', color: { rgb: 'E0E0E0' } },
      right: { style: 'thin', color: { rgb: 'E0E0E0' } }
    }
  };

  const firstColumnWidth = 18;
  const dataColumnWidth = 10;

  for (let c = 0; c < headerRow.length; c += 1) {
    const addr = encode({ r: headerRowIndex, c });
    if (ws[addr]) ws[addr].s = headerStyle;
  }

  for (let r = headerRowIndex + 1; r <= totalRowIndex; r += 1) {
    for (let c = 0; c < headerRow.length; c += 1) {
      const addr = encode({ r, c });
      if (!ws[addr]) continue;
      ws[addr].s = {
        ...(ws[addr].s || {}),
        ...borderStyle,
        alignment: {
          horizontal: c === 0 ? 'left' : 'right',
          vertical: 'center'
        },
        font: { sz: 11, color: { rgb: '1F1F1F' } }
      };
      if (c !== 0 && typeof ws[addr].v === 'number') {
        ws[addr].t = 'n';
        ws[addr].z = '#,##0';
      }
    }
  }

  ws['!cols'] = [
    { wch: firstColumnWidth },
    ...hourLabels.map(() => ({ wch: dataColumnWidth }))
  ];

  ws['!merges'] = merges;

  if (tableBlock.length > 1) {
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRowIndex, c: 0 },
        e: { r: totalRowIndex, c: headerRow.length - 1 }
      })
    };
  }

  try {
    ws['!freeze'] = { xSplit: 1, ySplit: headerRowIndex + 1 };
  } catch {
    // ignore freeze errors
  }

  XLSX.utils.book_append_sheet(wb, ws, 'AHA');
  return wb;
}

export default buildAhaWorkbook;
